/**
 * The djgg Firebase WEB configs — the ONE copy of the client config every game
 * web tier consumes (previously pasted into all five games' web
 * `platform/env.ts` files; R4-1 extraction).
 *
 * Two configs, selected by the caller's emulator decision (`@djgg/game-client`'s
 * `useEmulators()` in practice):
 *  - {@link EMULATOR_FIREBASE_CONFIG} — the `demo-djgg` project id the emulator
 *    suite treats as offline-only. Only the project id must match the suite; the
 *    apiKey/appId are placeholders the emulator ignores.
 *  - {@link LIVE_FIREBASE_CONFIG} — the real djgg-platform console config used
 *    by ANY hosted build (the portal SPA, Firebase Hosting under
 *    `/games/<slug>/`, creations proxied under `/play/**`) so guest auth,
 *    leaderboards, presence and online play hit the live project. The web
 *    apiKey is not a secret. Without this, a prod build kept `demo-key` and
 *    every auth call 400'd against identitytoolkit.
 *
 * {@link DEFAULT_WEB_EMULATORS} is the matching local emulator wiring
 * (auth 9099 / firestore 8080 / rtdb 9000 on localhost) — identical for every
 * game, so it lives here beside the configs it pairs with.
 */
import type { EmulatorConfig, FirebaseClientConfig } from './app.js';

/** Placeholder config for the Firebase emulator suite (`demo-djgg`). */
export const EMULATOR_FIREBASE_CONFIG: FirebaseClientConfig = {
  apiKey: 'demo-key',
  authDomain: 'demo-djgg.firebaseapp.com',
  projectId: 'demo-djgg',
  databaseURL: 'http://localhost:9000?ns=demo-djgg',
  storageBucket: 'demo-djgg.appspot.com',
  appId: 'demo-app-id',
};

/**
 * The REAL djgg-platform web config (hosted builds).
 *
 * `authDomain` is selected from the explicit set of attached HTTPS Hosting
 * domains below, not the `*.firebaseapp.com` default. Choosing the origin the
 * app itself runs on keeps the sign-in redirect SAME-ORIGIN. A cross-origin
 * authDomain puts the handler's storage in a third-party partition, which
 * Safari (iOS included, where every browser is WebKit) blocks — the redirect
 * returns with no session. Every domain in this allowlist also needs its
 * `/__/auth/handler` redirect URI registered on the project's OAuth web client.
 * The branded domain is the default. The Firebase alias remains in the
 * allowlist only for already-started OAuth callbacks on its reserved handler;
 * public pages redirect to the branded host before auth starts.
 */
const LIVE_AUTH_DOMAINS = new Set(['djgg-platform.web.app', 'djgg.jachai.dev']);

export function liveAuthDomain(): string {
  const hostname =
    typeof globalThis.location !== 'undefined' && globalThis.location.protocol === 'https:'
      ? globalThis.location.hostname
      : '';
  return LIVE_AUTH_DOMAINS.has(hostname) ? hostname : 'djgg.jachai.dev';
}

export const LIVE_FIREBASE_CONFIG: FirebaseClientConfig = {
  apiKey: 'AIzaSyDFQJ1zfxxNELfyQbstEaF9tPeSVg4WJCk',
  authDomain: liveAuthDomain(),
  projectId: 'djgg-platform',
  databaseURL: 'https://djgg-platform-default-rtdb.firebaseio.com',
  storageBucket: 'djgg-platform.firebasestorage.app',
  appId: '1:749639621430:web:12e2e462b08f233322b197',
};

/** The standard local emulator wiring every game web client uses. */
export const DEFAULT_WEB_EMULATORS: EmulatorConfig = {
  host: 'localhost',
  ports: { auth: 9099, firestore: 8080, database: 9000 },
};

/**
 * Supabase project constants — PUBLIC by design. The ONE definition now lives in
 * `@djgg/db` (next to `SupabaseDocStore`, its true home); re-exported here for
 * the callers that reach it through this firebase-carrying cloud barrel. A
 * firebase-free consumer imports it straight from `@djgg/db/webConfig`.
 */
export { SUPABASE_WEB } from '@djgg/db/webConfig';
